import React, { useState, useMemo, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";

import { Button, PageLoader } from "../styles/StyledComponents";
import { useAppData } from "../contexts/AppDataContext";
import {
  GitMerge, Activity, ChevronDown,
  FileText
} from "lucide-react";
import { trackFunnelEvent } from "../utils/funnelTracking";
import {
  getDomainIconComponent,
  getSubdomainIconComponent,
  getUniqueSubdomainIconComponents,
} from "../utils/taxonomyIcons";
import { useCert } from "../contexts/CertContext";
import { displayTaxonomyName } from "../utils/taxonomyDisplay";
import { readinessEvidenceHint } from "../utils/readinessPresentation";

// ─── Icon helpers ────────────────────────────────────────────────────────────

const getDomainIcon = (name = "") => {
  const Icon = getDomainIconComponent(name);
  return <Icon size={16} />;
};

const getSubdomainIcon = (name = "", IconOverride = null) => {
  const Icon = IconOverride || getSubdomainIconComponent(name);
  return <Icon size={14} />;
};

// ─── Color palette by hierarchy level ─────────────────────────────────────────

const LEVEL_COLORS = {
  domain:    "#06b6d4",
  subdomain: "#818cf8",
  topic:     "#a78bfa",
};

export const evidenceCount = (item = {}) => {
  const evidence = item.evidence || {};
  const count = Number(
    evidence.uniqueQuestions
      ?? evidence.answeredQuestions
      ?? evidence.attempts
      ?? item.answeredQuestions
      ?? item.attempts
      ?? 0
  );
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const isUnassessed = (item = {}) => evidenceCount(item) === 0;

// An item with no evidence shows nothing where the percentage goes. A blank is
// honestly empty; "0%" would read as a measured zero.
const assessedScoreLabel = (item = {}) => {
  const readiness = Number(item.readiness);
  if (!Number.isFinite(readiness) || readiness <= 0) return "0%";
  if (readiness < 1) return "<1%";
  return `${Math.round(Math.min(100, readiness))}%`;
};

export const scoreLabel = (item = {}) => isUnassessed(item) ? "" : assessedScoreLabel(item);

export const progressValue = (item = {}) => {
  if (isUnassessed(item)) return 0;
  const readiness = Number(item.readiness);
  return Number.isFinite(readiness) ? Math.min(100, Math.max(0, readiness)) : 0;
};

const normalizeKey = (value = "") =>
  String(value || "")
    .replace(/^\s*Domain\s+\d+\s*:\s*/i, "")
    .replace(/^\s*\d+\.\s*/, "")
    .trim()
    .toLowerCase();

const itemName = (item) => typeof item === "string" ? item : item?.name;

const readinessLookup = (items = [], nameProp = "name") => {
  const lookup = new Map();
  items.forEach((item) => {
    const name = item?.[nameProp] ?? item?.name ?? item?.subject;
    if (!name) return;
    const readiness = item?.readiness ?? item?.percentage ?? item?.A ?? 0;
    lookup.set(normalizeKey(name), Math.round(readiness || 0));
  });
  return lookup;
};

export const buildConfigHierarchy = (activeConfig, auditHierarchy = []) => {
  const domains = activeConfig?.domains || [];
  if (!domains.length) return [];

  const auditDomainScores = readinessLookup(auditHierarchy);

  return domains.map((domain) => {
    const domainKey = normalizeKey(domain.name);
    const auditDomain = auditHierarchy.find((item) => normalizeKey(item.name) === domainKey);
    const auditSubdomains = auditDomain?.sub_domains || [];

    return {
      name: domain.name,
      weight: domain.examWeight ?? domain.weight ?? null,
      readiness: auditDomainScores.get(domainKey) ?? 0,
      status: auditDomain?.status || "unassessed",
      masteredQuestions: auditDomain?.masteredQuestions || 0,
      answeredQuestions: auditDomain?.answeredQuestions || 0,
      eligibleQuestions: auditDomain?.eligibleQuestions || 0,
      evidence: auditDomain?.evidence || { uniqueQuestions: auditDomain?.answeredQuestions || 0 },
      sub_domains: (domain.sub_domains || []).map((subdomain) => {
        const subdomainName = itemName(subdomain);
        const subdomainKey = normalizeKey(subdomainName);
        const auditSubdomain = auditSubdomains.find((item) => normalizeKey(item.name) === subdomainKey);
        const auditTopicScores = readinessLookup(auditSubdomain?.topics || []);

        return {
          name: subdomainName,
          weight: subdomain.examWeight ?? subdomain.weight ?? null,
          readiness: auditSubdomain?.readiness ?? auditSubdomain?.percentage ?? 0,
          status: auditSubdomain?.status || "unassessed",
          masteredQuestions: auditSubdomain?.masteredQuestions || 0,
          answeredQuestions: auditSubdomain?.answeredQuestions || 0,
          eligibleQuestions: auditSubdomain?.eligibleQuestions || 0,
          evidence: auditSubdomain?.evidence || { uniqueQuestions: auditSubdomain?.answeredQuestions || 0 },
          topics: ((subdomain && typeof subdomain === "object" ? subdomain.topics : []) || []).map((topic) => {
            const topicName = itemName(topic);
            const auditTopic = auditSubdomain?.topics?.find((item) => normalizeKey(item.name) === normalizeKey(topicName));
            return {
              name: topicName,
              readiness: auditTopicScores.get(normalizeKey(topicName)) ?? 0,
              status: auditTopic?.status || "unassessed",
              masteredQuestions: auditTopic?.masteredQuestions || 0,
              answeredQuestions: auditTopic?.answeredQuestions || 0,
              eligibleQuestions: auditTopic?.eligibleQuestions || 0,
              evidence: auditTopic?.evidence,
            };
          }),
        };
      }),
    };
  });
};

// ─── Styled components ────────────────────────────────────────────────────────

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  h2 {
    font-size: 1.15rem;
    font-weight: 900;
    letter-spacing: 1.2px;
    color: #f8fafc;
    margin: 0;
  }
  span {
    font-size: 0.68rem;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 2px;
  }
`;

// ─── Domain card ──────────────────────────────────────────────────────────────

const DomainCardWrap = styled.div`
  background: #0f172a;
  border: 1px solid rgba(51, 65, 85, 0.35);
  border-radius: 14px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: border-color 0.2s;
  &:hover { border-color: rgba(51, 65, 85, 0.6); }
`;

const DomainHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 18px 24px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  user-select: none;
  gap: 14px;
  transition: background 0.15s;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  &:hover { background: ${({ $clickable }) => $clickable ? 'rgba(255,255,255,0.015)' : 'transparent'}; }

  @media (max-width: 600px) {
    padding: 14px;
    gap: 10px;
    align-items: flex-start;
  }
`;

const DomainIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ $color }) => $color}15;
  border: 1px solid ${({ $color }) => $color}30;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DomainName = styled.span`
  font-size: 0.86rem;
  font-weight: 780;
  color: #f8fafc;
  letter-spacing: -0.01em;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 0.74rem;
    line-height: 1.35;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
`;

const ScoreLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 800;
  color: ${({ $color }) => $color || '#f8fafc'};
  min-width: 58px;
  text-align: right;
  flex-shrink: 0;
`;

const ProgressBarTrack = styled.div`
  width: ${({ $width }) => $width || '120px'};
  height: ${({ $height }) => $height || '6px'};
  background: #1e293b;
  border-radius: 99px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: clamp(54px, 18vw, 78px);
  }
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: ${({ $color }) => $color || '#3b82f6'};
  box-shadow: 0 0 8px ${({ $color }) => $color || '#3b82f6'}50;
  transition: width 0.4s ease;
  min-width: ${({ $pct }) => $pct > 0 ? '3px' : '0'};
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ChevronIcon = styled(ChevronDown)`
  transition: transform 0.25s ease;
  color: #64748b;
  flex-shrink: 0;
  ${({ $open }) => $open && css`transform: rotate(180deg);`}
`;

const ChildrenWrap = styled.div`
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.25s ease;
  max-height: ${({ $open, $count }) => $open ? `${$count * 200}px` : '0'};
  opacity: ${({ $open }) => $open ? 1 : 0};
`;

// ─── Tree branch lines ─────────────────────────────────────────────────────

const TreeRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 24px 10px ${({ $depth }) => 24 + ($depth * 28)}px;
  gap: 10px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: background 0.12s;
  animation: ${fadeIn} 0.2s ease;
  position: relative;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  &:hover { background: rgba(255,255,255,0.012); }

  @media (max-width: 600px) {
    align-items: flex-start;
    padding: 10px 12px 10px ${({ $depth }) => 12 + ($depth * 18)}px;
    gap: 8px;
  }
`;

const TreeBranch = styled.div`
  position: absolute;
  left: ${({ $depth }) => 14 + ($depth * 28)}px;
  top: 0;
  bottom: 0;
  width: 14px;
  
  /* Vertical line from parent */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 1px;
    height: ${({ $isLast }) => $isLast ? '50%' : '100%'};
    background: ${({ $color }) => $color || '#334155'}40;
  }
  
  /* Horizontal connector */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 14px;
    height: 1px;
    background: ${({ $color }) => $color || '#334155'}40;
  }

  @media (max-width: 600px) {
    left: ${({ $depth }) => 6 + ($depth * 18)}px;
    width: 10px;
    &::after {
      width: 10px;
    }
  }
`;

const ParentLine = styled.div`
  position: absolute;
  left: ${({ $depth }) => 14 + ($depth * 28)}px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: ${({ $color }) => $color || '#334155'}40;

  @media (max-width: 600px) {
    left: ${({ $depth }) => 6 + ($depth * 18)}px;
  }
`;


const TreeIconWrap = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $color }) => $color}12;
  color: ${({ $color }) => $color};
`;

const TreeNodeName = styled.span`
  font-size: ${({ $size }) => $size || '0.75rem'};
  font-weight: 700;
  color: ${({ $muted }) => $muted ? '#64748b' : '#cbd5e1'};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 600px) {
    line-height: 1.35;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
`;

const TaxonomyText = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 600px) {
    gap: 6px;
    min-width: 104px;
    align-self: center;
  }
`;

// ─── SubdomainRow Component ──────────────────────────────────────────────────

function SubdomainRow({ sd, domainName, depth, isLast, color, IconComponent, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const topics = sd.topics || [];
  const pct = progressValue(sd);
  const hasTopics = topics.length > 0;
  const toggle = () => hasTopics && setOpen(o => !o);

  return (
    <>
      <TreeRow
        as={hasTopics ? "button" : "div"}
        type={hasTopics ? "button" : undefined}
        $depth={depth}
        $clickable={hasTopics}
        onClick={toggle}
        aria-expanded={hasTopics ? open : undefined}
      >
        <TreeBranch $depth={depth} $isLast={isLast} $color={color} />
        <TreeIconWrap $color={color}>
          {getSubdomainIcon(sd.name, IconComponent)}
        </TreeIconWrap>
        <TaxonomyText>
          <TreeNodeName>{displayTaxonomyName(sd.name)}</TreeNodeName>
        </TaxonomyText>
        <RightGroup>
          <ScoreLabel $color={color} title={readinessEvidenceHint(sd.evidence || sd)}>{scoreLabel(sd)}</ScoreLabel>
          <ProgressBarTrack $width="100px" role="progressbar" aria-label={`${displayTaxonomyName(sd.name)} readiness`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={pct}>
            <ProgressBarFill $color={color} $pct={pct} style={{ width: `${pct}%` }} />
          </ProgressBarTrack>
          {hasTopics && <ChevronIcon size={14} $open={open} />}
        </RightGroup>
      </TreeRow>
      {hasTopics && (
        <ChildrenWrap $open={open} $count={topics.length}>
          {topics.map((topic, tIdx) => (
            <TopicRow
              key={topic.name}
              topic={topic}
              depth={depth + 1}
              isLast={tIdx === topics.length - 1}
              color={LEVEL_COLORS.topic}
            />
          ))}
        </ChildrenWrap>
      )}
    </>
  );
}

// ─── TopicRow Component ──────────────────────────────────────────────────────

function TopicRow({ topic, depth, isLast, color }) {
  const pct = progressValue(topic);

  return (
    <TreeRow $depth={depth} $clickable={false}>
      <ParentLine $depth={1} $color={LEVEL_COLORS.subdomain} />
      <TreeBranch $depth={depth} $isLast={isLast} $color={color} />
      <TreeIconWrap $color={color}>
        <FileText size={12} />
      </TreeIconWrap>
      <TaxonomyText>
        <TreeNodeName $size="0.72rem">{displayTaxonomyName(topic.name)}</TreeNodeName>
      </TaxonomyText>
      <RightGroup>
        <ScoreLabel $color={color} title={readinessEvidenceHint(topic.evidence || topic)}>{scoreLabel(topic)}</ScoreLabel>
        <ProgressBarTrack $width="80px" role="progressbar" aria-label={`${displayTaxonomyName(topic.name)} readiness`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={pct}>
          <ProgressBarFill $color={color} $pct={pct} style={{ width: `${pct}%` }} />
        </ProgressBarTrack>
      </RightGroup>
    </TreeRow>
  );
}

// ─── DomainCard Component ────────────────────────────────────────────────────

function DomainCard({ domain, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const pct = progressValue(domain);
  const domainColor = LEVEL_COLORS.domain;
  const subdomains = domain.sub_domains || [];
  const subdomainIcons = getUniqueSubdomainIconComponents(subdomains.map((sd) => sd.name));

  const totalChildren = useMemo(() => {
    return (domain.sub_domains || []).reduce((acc, sd) => {
      const topics = sd.topics || [];
      return acc + 1 + topics.length;
    }, 0);
  }, [domain.sub_domains]);
  const hasStructure = totalChildren > 0;
  const toggle = () => hasStructure && setOpen(o => !o);

  return (
    <DomainCardWrap>
      <DomainHeader
        as={hasStructure ? "button" : "div"}
        type={hasStructure ? "button" : undefined}
        onClick={toggle}
        aria-expanded={hasStructure ? open : undefined}
        $clickable={hasStructure}
      >
        <DomainIconWrap $color={domainColor}>
          {getDomainIcon(domain.name)}
        </DomainIconWrap>
        <TaxonomyText>
          <DomainName>{displayTaxonomyName(domain.name)}</DomainName>
        </TaxonomyText>
        <RightGroup>
          <ScoreLabel $color={domainColor} title={readinessEvidenceHint(domain.evidence || domain)}>{scoreLabel(domain)}</ScoreLabel>
          <ProgressBarTrack $width="140px" $height="7px" role="progressbar" aria-label={`${displayTaxonomyName(domain.name)} readiness`} aria-valuemin="0" aria-valuemax="100" aria-valuenow={pct}>
            <ProgressBarFill $color={domainColor} $pct={pct} style={{ width: `${pct}%` }} />
          </ProgressBarTrack>
          {hasStructure && <ChevronIcon size={16} $open={open} />}
        </RightGroup>
      </DomainHeader>

      <ChildrenWrap $open={open} $count={totalChildren}>
        {subdomains.map((sd, sdIdx) => (
          <SubdomainRow
            key={sd.name}
            sd={sd}
            domainName={domain.name}
            depth={1}
            isLast={sdIdx === subdomains.length - 1}
            color={LEVEL_COLORS.subdomain}
            IconComponent={subdomainIcons[sdIdx]}
          />
        ))}
      </ChildrenWrap>
    </DomainCardWrap>
  );
}

// ─── ReadinessBreakdown Section ─────────────────────────────────────────────

function ReadinessBreakdown({ hierarchy, hasFullAccess }) {
  return (
    <>
      <PageHeader>
        <HeaderTitle>
          <GitMerge size={20} color="#3b82f6" />
          <div>
            <h2>Readiness Breakdown</h2>
            <span>{hasFullAccess ? 'Domain, Subdomain and Topic' : 'Domain performance'}</span>
          </div>
        </HeaderTitle>
      </PageHeader>

      {hierarchy.length > 0 ? (
        hierarchy.map((domain) => (
          <DomainCard
            key={domain.name}
            domain={domain}
          />
        ))
      ) : (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#64748b', fontSize: '0.9rem' }}>
          Complete a session to populate your domain breakdown.
        </div>
      )}
    </>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Analytics() {
    const {
      auditData,
      auditError,
      analyticsDataStale,
      loading,
      refreshAnalytics,
    } = useAppData();
    const { activeCert, EXAM_CONFIGS } = useCert();
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        trackFunnelEvent("analytics_opened");
    }, []);

    const auditHierarchy = auditData?.blueprint_hierarchy || [];
    const activeConfig = EXAM_CONFIGS?.[activeCert];
    const configHierarchy = buildConfigHierarchy(activeConfig, auditHierarchy);
    const fullHierarchy = configHierarchy.length ? configHierarchy : auditHierarchy;
    const hierarchy = fullHierarchy;
    const retryAnalytics = async () => {
        setRetrying(true);
        try { await refreshAnalytics(); }
        catch { /* Keep the retry control available on unexpected failures. */ }
        finally { setRetrying(false); }
    };

    if (loading && !auditData) {
        return <PageLoader label="Loading your analytics…" />;
    }

    // Dashboard data cannot stand in for analytics evidence. If the audit
    // request failed, say so and offer recovery rather than presenting a
    // healthy-looking but empty readiness tree.
    if (!auditData && !loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 20px', color: '#94a3b8' }}>
                <Activity size={48} style={{ marginBottom: 20, opacity: 0.2 }} />
                <h3>Could Not Load Analytics</h3>
                <p>{auditError
                    ? "We had trouble fetching your analytics. Check your connection and try again."
                    : "Analytics data is unavailable. Try loading it again."}</p>
                <Button
                    type="button"
                    $primary
                    disabled={retrying}
                    onClick={retryAnalytics}
                    style={{ marginTop: 24, padding: '12px 24px' }}
                >
                    {retrying ? 'Retrying…' : 'Retry'}
                </Button>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }}>
            {analyticsDataStale && (
                <div
                    role="alert"
                    style={{
                      marginBottom: 16,
                      padding: '12px 14px',
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      borderRadius: 8,
                      background: 'rgba(245, 158, 11, 0.08)',
                      color: '#fef3c7',
                      fontSize: '0.8rem',
                    }}
                >
                    Showing your last available analytics because the latest refresh failed.
                    <Button
                        type="button"
                        disabled={retrying}
                        onClick={retryAnalytics}
                        style={{ marginLeft: 12, padding: '6px 10px' }}
                    >
                        {retrying ? 'Retrying…' : 'Retry'}
                    </Button>
                </div>
            )}
            <ReadinessBreakdown
                hierarchy={hierarchy}
                hasFullAccess={true}
            />
        </div>
    );
}
